import type React from 'react';
import { Section } from '@/components/ui/Section';
import { Search, Layers, PenTool, Sparkles, Image as ImageIcon, Share2, type LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { HandDrawnCheck } from '@/components/ui/decorative';

interface FeatureItemProps {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly description: string;
}

const FeatureItem: React.FC<FeatureItemProps> = ({ icon: Icon, title, description }) => (
  <div className="py-5 first:pt-0 last:pb-0">
    <h4 className="text-white text-lg md:text-xl font-display font-semibold mb-2 flex items-center gap-3">
      <Icon className="w-4 h-4 text-champagne shrink-0" strokeWidth={1.75} aria-hidden="true" />
      {title}
    </h4>
    <p className="text-cream-paper/70 text-[15px] leading-relaxed pl-7">{description}</p>
  </div>
);

const FEATURES: ReadonlyArray<FeatureItemProps> = [
  {
    icon: Search,
    title: 'Smart Source Discovery',
    description: 'AI finds credible, relevant sources in your industry automatically.',
  },
  {
    icon: Layers,
    title: 'Intelligent Synthesis',
    description: 'Key insights extracted and organized for easy reference.',
  },
  {
    icon: PenTool,
    title: 'Style Consistency',
    description: 'Custom guidelines ensure every article sounds like you, not a bot.',
  },
  {
    icon: Sparkles,
    title: 'Content Inspiration',
    description: 'Transform web articles into strategic content angles. Choose from 4 frameworks: Contrarian, Value, Authority, Growth.',
  },
  {
    icon: ImageIcon,
    title: 'Professional Header Images',
    description: 'Publication-quality article images in 23+ styles — Economist, Bloomberg, Financial Times — across 10 aspect ratios.',
  },
  {
    icon: Share2,
    title: 'Multi-Platform Distribution',
    description: 'Convert articles into LinkedIn posts, Twitter threads, and email. Export to Markdown, Word, or publish directly — you own the output.',
  },
];

const DraftPreview: React.FC = () => (
  <div className="relative w-full max-w-md">
    {/* Paper sheet with editorial draft preview */}
    <div className="relative bg-cream-paper text-charcoal p-8 md:p-10 shadow-2xl border border-charcoal/10">
      {/* Eyebrow */}
      <div className="flex items-center gap-3 mb-5">
        <div className="h-px w-6 bg-bottle-green" />
        <span className="smcp text-[11px] text-muted-warm font-medium">
          Working draft
        </span>
      </div>

      {/* Headline */}
      <h3 className="font-display text-2xl font-semibold leading-tight tracking-tight mb-5">
        Why most AI articles win the easy points and miss the case.
      </h3>

      {/* Opening prose */}
      <p className="font-serif text-[13px] leading-relaxed text-charcoal/85 mb-3">
        In a saturated content market, the gap between &ldquo;competent&rdquo; and
        &ldquo;authoritative&rdquo; is widening. Anyone can produce a 1,200-word post in
        twenty minutes&nbsp;<sup className="text-bottle-green font-semibold">[1]</sup>;
        few can produce one a senior buyer would forward to their team.
      </p>

      {/* Inline annotations */}
      <div className="my-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-bottle-green/90">
        <span className="inline-flex items-center gap-1.5">
          <HandDrawnCheck className="w-3.5 h-3.5" />
          <span className="smcp font-medium">Voice match</span>
        </span>
        <span className="text-aged-brass/60" aria-hidden="true">&middot;</span>
        <span className="inline-flex items-center gap-1.5">
          <HandDrawnCheck className="w-3.5 h-3.5" />
          <span className="smcp font-medium">7 sources cited</span>
        </span>
        <span className="text-aged-brass/60" aria-hidden="true">&middot;</span>
        <span className="inline-flex items-center gap-1.5">
          <HandDrawnCheck className="w-3.5 h-3.5" />
          <span className="smcp font-medium">Insight density: high</span>
        </span>
      </div>

      <p className="font-serif text-[13px] leading-relaxed text-charcoal/70 line-clamp-3">
        The difference is not in the prose itself but in the citation density, the
        depth of research underneath each claim, and the editorial discipline that
        knows when an anecdote is doing real work and when it&hellip;
      </p>

      {/* Footer */}
      <div className="mt-8 pt-4 border-t border-aged-brass/30 flex items-center justify-between smcp text-[11px] text-muted-warm font-medium">
        <span>Draft v2.1 &middot; 1,847 words</span>
        <span>Reviewed 3 min ago</span>
      </div>
    </div>

    {/* Offset frame echo of DraftMockup */}
    <div
      className="absolute -bottom-3 -right-3 w-full h-full border border-aged-brass/40 pointer-events-none -z-10"
      aria-hidden="true"
    />
  </div>
);

export const Features: React.FC = () => {
  return (
    <Section className="bg-charcoal text-cream-paper relative overflow-hidden">
      {/* Decorative background text */}
      <div className="absolute right-0 top-1/4 text-[120px] font-display font-bold text-white/[0.02] select-none pointer-events-none leading-none tracking-tighter hidden lg:block">
        WRITE
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start relative z-10">
        <div>
          <div className="mb-12">
            <Badge variant="outline" className="mb-8 border-champagne/30 text-champagne hover:bg-aged-brass/10">
              Capabilities
            </Badge>
            <h2 className="font-display font-semibold tracking-tight leading-[1.1] text-white mb-6 text-[clamp(2rem,4vw+0.5rem,3rem)]">
              Research, structure, write.
            </h2>
            <p className="font-body text-lg text-cream-paper/80 leading-relaxed font-normal max-w-xl">
              From topic to publication in minutes &mdash; with citations, voice consistency,
              and the artifacts you need to ship.
            </p>
          </div>

          <ul className="divide-y divide-white/5">
            {FEATURES.map((feature) => (
              <li key={feature.title}>
                <FeatureItem
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                />
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center justify-center lg:justify-end">
          <DraftPreview />
        </div>
      </div>
    </Section>
  );
};
